import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { regenerateRecoveryCodes } from '../../api';
import './MfaDeviceReplaceModal.css';

const STEP_INPUT = 'input';
const STEP_CODES = 'codes';

const MfaRecoveryCodesRegenerateModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(STEP_INPUT);
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [hasAcknowledgedCodes, setHasAcknowledgedCodes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const cooldownTimerRef = useRef(null);

  // Clear countdown interval safely
  const clearCooldownTimer = () => {
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
  };

  // Live countdown timer for authoritative cooldown
  useEffect(() => {
    if (cooldownSeconds > 0) {
      cooldownTimerRef.current = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            clearCooldownTimer();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearCooldownTimer();
  }, [cooldownSeconds]);

  // Teardown / reset all state on open, close, or unmount
  useEffect(() => {
    if (isOpen) {
      setStep(STEP_INPUT);
      setPassword('');
      setTotpCode('');
      setRecoveryCodes([]);
      setHasAcknowledgedCodes(false);
      setIsSubmitting(false);
      setErrorMessage('');
      setInfoMessage('');
      setIsNetworkError(false);
      setCooldownSeconds(0);
      clearCooldownTimer();
    }
    return () => {
      clearCooldownTimer();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (isSubmitting) return;

    // Block accidental close on one-time recovery codes screen without saving
    if (step === STEP_CODES && !hasAcknowledgedCodes) {
      setErrorMessage('Please save your new recovery codes and check the confirmation box before closing.');
      return;
    }

    // Wipe sensitive state in memory
    setPassword('');
    setTotpCode('');
    setRecoveryCodes([]);
    clearCooldownTimer();
    onClose();
  };

  // Submit Password + TOTP to Regenerate Recovery Codes
  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPassword = password.trim();
    const cleanTotp = totpCode.trim();

    if (!cleanPassword || cleanTotp.length !== 6 || isSubmitting || cooldownSeconds > 0) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setInfoMessage('');
    setIsNetworkError(false);

    try {
      const data = await regenerateRecoveryCodes(cleanPassword, cleanTotp);
      const returnedCodes = Array.isArray(data.recovery_codes) ? data.recovery_codes : [];

      // Wipe credentials immediately upon successful response
      setPassword('');
      setTotpCode('');

      setRecoveryCodes(returnedCodes);
      setStep(STEP_CODES);
    } catch (err) {
      if (!err.response) {
        // Network / timeout error: outcome is ambiguous. Do not blindly resubmit!
        setIsNetworkError(true);
        setErrorMessage(
          'A network error occurred while communicating with the server. If the server processed your request, your recovery codes may have been rotated. Do not blindly resubmit. Please check your profile status or wait 60 seconds before attempting again.'
        );
      } else {
        const status = err.response.status;
        const errData = err.response.data || {};

        if (status === 429 && errData.code === 'recovery_code_cooldown_active') {
          // Authoritative 60-second cooldown
          const retryAfter = Number(errData.retry_after) || Number(err.response.headers?.['retry-after']) || 60;
          setCooldownSeconds(retryAfter);
          setErrorMessage(
            errData.detail || `Recovery codes were recently regenerated. Please wait ${retryAfter}s before regenerating again.`
          );
        } else if (status === 401) {
          setErrorMessage(
            errData.detail || 'Your session has expired or been terminated. Please sign in again.'
          );
        } else {
          setErrorMessage(
            errData.detail || errData.message || 'Failed to regenerate recovery codes. Please check your credentials and try again.'
          );
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy Recovery Codes to Clipboard
  const handleCopyRecoveryCodes = () => {
    if (recoveryCodes.length === 0) return;
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setInfoMessage('All recovery codes copied to clipboard!');
      setTimeout(() => setInfoMessage(''), 3000);
    });
  };

  // Download Recovery Codes as .txt file
  const handleDownloadRecoveryCodes = () => {
    if (recoveryCodes.length === 0) return;
    const text =
      `SALESROUTE SUPERADMIN RECOVERY CODES\n` +
      `Regenerated: ${new Date().toISOString()}\n\n` +
      `Keep these 10 one-time recovery codes in a secure location.\n` +
      `Each code can only be used once.\n` +
      `All previous recovery codes are now INVALID.\n\n` +
      recoveryCodes.map((code, index) => `${index + 1}. ${code}`).join('\n') +
      `\n\nDO NOT SHARE THESE CODES WITH ANYONE.\n` +
      `Plaintext codes cannot be recovered from server hashes if lost.`;

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'salesroute-superadmin-recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Final Acknowledgement
  const handleComplete = () => {
    if (!hasAcknowledgedCodes) return;
    // Wipe recovery codes from memory
    setRecoveryCodes([]);
    clearCooldownTimer();
    onSuccess();
  };

  return (
    <div className="mfa-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="mfa-regen-title">
      <div className="mfa-modal-container">
        {/* Header */}
        <div className="mfa-modal-header">
          <div className="mfa-modal-header-text">
            <h3 id="mfa-regen-title">
              {step === STEP_INPUT && 'Regenerate Recovery Codes'}
              {step === STEP_CODES && 'Save New Recovery Codes'}
            </h3>
            <p>
              {step === STEP_INPUT && 'Authenticate with your password and current TOTP code to generate a new batch'}
              {step === STEP_CODES && 'Important: Store these one-time codes in a secure location'}
            </p>
          </div>
          {step !== STEP_CODES && (
            <button
              type="button"
              className="mfa-modal-close-btn"
              onClick={handleClose}
              disabled={isSubmitting}
              aria-label="Close modal"
              data-testid="mfa-regen-close-btn"
            >
              ×
            </button>
          )}
        </div>

        {/* Body */}
        <div className="mfa-modal-body">
          {errorMessage && (
            <div
              className={`mfa-modal-banner ${isNetworkError ? 'mfa-modal-banner-warning' : 'mfa-modal-banner-error'}`}
              role="alert"
              data-testid="mfa-regen-error"
            >
              {errorMessage}
            </div>
          )}

          {infoMessage && (
            <div className="mfa-modal-banner mfa-modal-banner-info" data-testid="mfa-regen-info">
              {infoMessage}
            </div>
          )}

          {cooldownSeconds > 0 && (
            <div className="mfa-modal-banner mfa-modal-banner-warning" data-testid="mfa-regen-cooldown-banner">
              ⏱ <strong>Cooldown active:</strong> Please wait {cooldownSeconds} seconds before requesting another regeneration.
            </div>
          )}

          {/* STEP 1: Password and TOTP Input */}
          {step === STEP_INPUT && (
            <form onSubmit={handleSubmit} data-testid="mfa-regen-form">
              <div className="mfa-modal-banner mfa-modal-banner-warning">
                <strong>Invalidation Notice:</strong> Generating a new set of recovery codes will immediately invalidate all existing recovery codes. Secondary active sessions will also be revoked.
              </div>

              <div className="mfa-input-group mt-15">
                <label className="mfa-input-label" htmlFor="mfa-regen-password">
                  Current SuperAdmin Password
                </label>
                <input
                  id="mfa-regen-password"
                  type="password"
                  className="mfa-text-input"
                  placeholder="Enter current password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  autoFocus
                  required
                  data-testid="mfa-regen-password-input"
                />
              </div>

              <div className="mfa-input-group">
                <label className="mfa-input-label" htmlFor="mfa-regen-totp">
                  6-Digit Authenticator Code (TOTP)
                </label>
                <input
                  id="mfa-regen-totp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  className="mfa-totp-code-input"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={isSubmitting}
                  autoComplete="one-time-code"
                  required
                  data-testid="mfa-regen-totp-input"
                />
              </div>

              <div className="mfa-modal-footer">
                <button
                  type="button"
                  className="mfa-btn-secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  data-testid="mfa-regen-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="mfa-btn-primary"
                  disabled={
                    isSubmitting ||
                    !password.trim() ||
                    totpCode.trim().length !== 6 ||
                    cooldownSeconds > 0
                  }
                  data-testid="mfa-regen-submit-btn"
                >
                  {isSubmitting ? 'Regenerating...' : 'Regenerate Codes'}
                </button>
              </div>
            </form>
          )}

          {/* STEP 2: One-Time Display of Regenerated Recovery Codes */}
          {step === STEP_CODES && (
            <div className="mfa-recovery-codes-section" data-testid="mfa-regen-codes-section">
              <div className="mfa-modal-banner mfa-modal-banner-success">
                <strong>Recovery codes successfully regenerated!</strong> All previous recovery codes have been revoked and replaced.
              </div>

              <div className="mfa-modal-banner mfa-modal-banner-warning">
                <strong>CRITICAL:</strong> Below are your 10 new emergency recovery codes. These codes are displayed <u>ONLY ONCE</u> and cannot be retrieved from server hashes if lost. Store them in a secure password manager.
              </div>

              <div className="mfa-recovery-codes-grid" data-testid="mfa-regen-codes-grid">
                {recoveryCodes.map((code, index) => (
                  <div key={index} className="mfa-recovery-code-card">
                    <span className="mfa-code-num">{index + 1}.</span>
                    <span className="mfa-code-txt">{code}</span>
                  </div>
                ))}
              </div>

              <div className="mfa-recovery-code-actions">
                <button
                  type="button"
                  className="mfa-recovery-action-btn"
                  onClick={handleCopyRecoveryCodes}
                  data-testid="mfa-regen-copy-all-btn"
                >
                  📋 Copy All Codes
                </button>
                <button
                  type="button"
                  className="mfa-recovery-action-btn"
                  onClick={handleDownloadRecoveryCodes}
                  data-testid="mfa-regen-download-btn"
                >
                  💾 Download as .txt
                </button>
              </div>

              <label className="mfa-acknowledgement-box" data-testid="mfa-regen-acknowledgement-box">
                <input
                  type="checkbox"
                  checked={hasAcknowledgedCodes}
                  onChange={(e) => setHasAcknowledgedCodes(e.target.checked)}
                  data-testid="mfa-regen-acknowledge-checkbox"
                />
                <span>
                  I have saved these 10 recovery codes in a secure location and understand they cannot be viewed again.
                </span>
              </label>

              <div className="mfa-modal-footer">
                <button
                  type="button"
                  className="mfa-btn-primary w-100"
                  disabled={!hasAcknowledgedCodes}
                  onClick={handleComplete}
                  data-testid="mfa-regen-complete-btn"
                >
                  I Have Saved My Codes — Return to Profile
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

MfaRecoveryCodesRegenerateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default MfaRecoveryCodesRegenerateModal;
